<script lang="ts">
	import { format } from 'svelte-i18n';
	//Design system components
	import { Icon, IconButton, Tooltip } from '@Shelly/shelly-design-system';
	//Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import {
		getTemplateComponents,
		translateTemplateTitle,
	} from 'src/helpers/xt1_helpers/template_helpers';
	//Components
	import HumidityWheel from '../Thermostat/ThermostatHumidityWheel.svelte';
	import ModeModal from '../Thermostat/ThermostatModeModal.svelte';
	import TemplateToggleSettings from '../Components/TemplateToggleSettings.svelte';
	//Types
	import { AdvancedCard } from '@Shelly/shelly-design-system';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { Card } from 'src/components/SelectList/SelectList.types';
	import type { TemplateComponent, VCInterfaceStatus } from 'src/types/VirtualComponents';
	import { getWorkingModeIcon } from 'src/helpers/device_data_helpers';

	export let device: Device, statuses: VCInterfaceStatus[], mode: string;

	const workingModeRole = 'working_mode';
	const fanSpeedRole = 'fan_speed';
	const lightModeRole = 'light_mode';
	const fanIconsMapper = {
		auto: 'fas fa-a',
		low: 'fak fa-solid-fan-speed-low',
		medium: 'fak fa-solid-fan-speed-medium',
		high: 'fak fa-solid-fan-speed-high',
	};

	const labelFanSpeed = $format('label_fan_speed', {
		default: `Fan speed`,
	});
	const labelChooseFan = $format('label_fan_choose', {
		default: 'Choose your fan speed',
	});

	const labelLightMode = $format('xt1_template_light_mode_mode', {
		default: `Light mode`,
	});
	const labelChooseLightMode = $format('xt1_template_light_mode_choose', {
		default: 'Choose your light mode work mode',
	});

	let waitForWorkingModesResponse = '';
	let waitForLightModeResponse = false;
	let waitForFanModeResponse = false;
	let targetLockWorkingMode = false;
	let fanSpeedLock = false;
	let lightModeLock = false;
	let workingModes: TemplateComponent;
	let fanSpeedModes: TemplateComponent;
	let lightModes: TemplateComponent;
	let workingModesOptions: string[];
	let fanSpeedOptions: string[];
	let lightModeOptions: string[];
	let lightMode: string;
	let fanSpeedMode: string;
	let showLightModeModal = false;
	let showFanModeModal = false;
	let tooltipReset = 0;

	const getFanSpeedOptions = (options: string[]): Card[] => {
		return (options || []).map((i) => {
			return {
				id: i,
				name: $format('label_fan_mode_' + i, {
					default: capFirstChar(i),
				}),
				selected: false,
				icon: fanIconsMapper[i],
			};
		});
	};

	const getLightModeOptions = (options: string[]): Card[] => {
		return (options || []).map((i) => {
			return {
				id: i,
				name: translateTemplateTitle(lightModes?.config?.meta?.ui?.titles?.[i] || i),
				selected: false,
				icon: fanIconsMapper[i],
			};
		});
	};

	const onSettingChange = (device: Device) => {
		workingModes = getTemplateComponents(device, null, new RegExp(workingModeRole))?.[0];
		const _mode = statuses.find((i) => new RegExp(workingModeRole).test(i.role))?.event;
		fanSpeedModes = getTemplateComponents(device, null, new RegExp(fanSpeedRole))?.[0];
		lightModes = getTemplateComponents(device, null, new RegExp(lightModeRole))?.[0];
		const _fanSpeedMode = statuses.find((i) => new RegExp(fanSpeedRole).test(i.role))?.event;
		const _lightMode = statuses.find((i) => new RegExp(lightModeRole).test(i.role))?.event;

		const _options = workingModes?.config?.options || [];
		const _fanSpeedOptions = fanSpeedModes?.config?.options || [];
		const _lightModeOptions = lightModes?.config?.options || [];
		if (workingModesOptions != _options) {
			workingModesOptions = _options;
		}
		if (fanSpeedOptions != _fanSpeedOptions) {
			fanSpeedOptions = _fanSpeedOptions;
		}
		if (lightModeOptions != _lightModeOptions) {
			lightModeOptions = _lightModeOptions;
		}
		if (!waitForWorkingModesResponse && !targetLockWorkingMode && mode !== _mode) {
			mode = _mode;
		}
		if (targetLockWorkingMode && mode == _mode) {
			targetLockWorkingMode = false;
		}
		if (!waitForLightModeResponse && !lightModeLock && lightMode != _lightMode) {
			lightMode = _lightMode;
		}
		if (lightModeLock && lightMode == _lightMode) {
			lightModeLock = false;
		}
		if (!waitForFanModeResponse && !fanSpeedLock && fanSpeedMode != _fanSpeedMode) {
			fanSpeedMode = _fanSpeedMode;
		}
		if (fanSpeedLock && fanSpeedMode == _fanSpeedMode) {
			fanSpeedLock = false;
		}
	};

	const saveWorkingModes = async (option: string) => {
		waitForWorkingModesResponse = option;
		const res: SettingRequest = await gen2Request(device, `${workingModes.type}.set`, {
			id: workingModes.config.id,
			value: option,
		});
		if (res?.isok) {
			targetLockWorkingMode = true;
			mode = option;
		} else {
			onSettingsResponse(res, device);
		}
		waitForWorkingModesResponse = '';
	};

	const saveFanSpeedMode = async (option: string) => {
		// Send req only when new value selected
		if (option !== fanSpeedMode) {
			waitForFanModeResponse = true;
			const res: SettingRequest = await gen2Request(device, `${fanSpeedModes.type}.set`, {
				id: fanSpeedModes.config.id,
				value: option,
			});
			if (res?.isok) {
				fanSpeedLock = true;
				fanSpeedMode = option;
			} else {
				onSettingsResponse(res, device);
			}
			waitForFanModeResponse = false;
		}

		onCloseFanModeModal();
	};

	const saveLightMode = async (option: string) => {
		// Send req only when new value selected
		if (option !== lightMode) {
			waitForLightModeResponse = true;
			const res: SettingRequest = await gen2Request(device, `${lightModes.type}.set`, {
				id: lightModes.config.id,
				value: option,
			});
			if (res?.isok) {
				lightModeLock = true;
				lightMode = option;
			} else {
				onSettingsResponse(res, device);
			}
			waitForLightModeResponse = false;
		}

		onCloseLightModeModal();
	};

	const getModeTitle = (cardList: Card[], selectedValue: string | number) => {
		return cardList.find((card) => card.id == selectedValue)?.name || cardList[0]?.name;
	};

	const onCloseLightModeModal = () => {
		showLightModeModal = false;
	};

	const onCloseFanModeModal = () => {
		showFanModeModal = false;
	};

	$: onSettingChange(device);
	$: fanModeOptions = getFanSpeedOptions(fanSpeedOptions);
	$: lightOptions = getLightModeOptions(lightModeOptions);
</script>

{#if workingModes && mode != null}
	<HumidityWheel {device} {statuses} />
	<div class="d-flex justify-content-center mb-m working-modes-wrapper">
		{#key tooltipReset}
			{#each workingModesOptions as workingMode}
				<Tooltip
					placement="top"
					title={translateTemplateTitle(
						workingModes?.config?.meta?.ui?.titles?.[workingMode] || workingMode,
					)}
				>
					<IconButton
						aria-label={$format('button_set_mode', {
							default: 'Set mode',
						})}
						class="ml-xs mr-xs"
						style={`${
							mode === workingMode || waitForWorkingModesResponse == workingMode
								? 'pointer-events:none;'
								: ''
						}`}
						loading={waitForWorkingModesResponse == workingMode}
						variant={`${mode === workingMode ? 'primary' : 'secondary'}`}
						icon={getWorkingModeIcon(workingMode)}
						on:click={(e) => {
							saveWorkingModes(workingMode);
							tooltipReset += 1;
						}}
					/></Tooltip
				>
			{/each}
		{/key}
	</div>

	{#if lightOptions.length > 0}
		<AdvancedCard
			aria-label={getModeTitle(lightOptions, lightMode)}
			class="mb-s"
			title={getModeTitle(lightOptions, lightMode)}
			subtitle={{
				value: labelLightMode,
				bold: false,
				type: 'muted',
			}}
			avatar={{ icon: 'fal fa-lightbulb-on' }}
			isCondensed
			isFocusable
			on:click={() => (showLightModeModal = true)}
		>
			<Icon class="align-self-center" icon="fas fa-chevron-right" size="s" slot="right" />
		</AdvancedCard>

		<ModeModal
			on:save={(e) => {
				saveLightMode(e.detail['selectedValue']);
			}}
			on:close={onCloseLightModeModal}
			showModal={showLightModeModal}
			title={labelLightMode}
			heading={labelChooseLightMode}
			selectedValue={lightMode}
			itemsList={lightOptions}
			isLoading={waitForLightModeResponse}
		/>
	{/if}

	{#if fanModeOptions.length > 0}
		<AdvancedCard
			aria-label={getModeTitle(fanModeOptions, fanSpeedMode)}
			title={getModeTitle(fanModeOptions, fanSpeedMode)}
			class="mb-s"
			subtitle={{
				value: labelFanSpeed,
				bold: false,
				type: 'muted',
			}}
			avatar={{ icon: fanIconsMapper[fanSpeedMode] || fanIconsMapper.auto }}
			isCondensed
			isFocusable
			on:click={() => (showFanModeModal = true)}
		>
			<Icon class="align-self-center" icon="fas fa-chevron-right" size="s" slot="right" />
		</AdvancedCard>

		<ModeModal
			on:save={(e) => {
				saveFanSpeedMode(e.detail['selectedValue']);
			}}
			on:close={onCloseFanModeModal}
			showModal={showFanModeModal}
			title={labelFanSpeed}
			heading={labelChooseFan}
			selectedValue={fanSpeedMode}
			itemsList={fanModeOptions}
			isLoading={waitForFanModeResponse}
		/>
	{/if}
{/if}

<AdvancedCard
	title={$format('sub_settings_title_kids_protection', {
		default: 'Child lock',
	})}
	size="s"
	class="mb-s"
	avatar={{ icon: 'fat fa-lock' }}
	isCondensed
>
	<div class="align-self-center" slot="right">
		<TemplateToggleSettings {device} setting="child_lock" title="" />
	</div>
</AdvancedCard>
<AdvancedCard
	title={$format('xt1_template_enable_uv', {
		default: 'Enable UV',
	})}
	size="s"
	avatar={{ icon: 'far fa-soap' }}
	isCondensed
>
	<div class="align-self-center" slot="right">
		<TemplateToggleSettings {device} setting="enable_uv" title="" />
	</div>
</AdvancedCard>

<style>
	.working-modes-wrapper {
		z-index: 3;
	}
</style>
